import { Box, IconButton, Paper, Typography } from "@material-ui/core";
import { Add as AddIcon, Edit as EditIcon } from "@material-ui/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import DueIcon from "../../../components/icons/FormStatus/DueIcon";
import OverdueIcon from "../../../components/icons/FormStatus/OverdueIcon";
import { tableText, useMapThemeClasses } from "../../../hooks/use-map-theme-styles";
import { FlexFormStatus, Maybe, OccurrenceEnum, TimeframeRangeType, User } from "../../../models/generated/graphql";
import { localeDate } from "../../../utils/locale-date";
import startUpper from "../../../utils/start-upper";
import useStyles from "../use-styles";
import { GetCaseByIdQuery } from "../types/queries.generated";

export type CaseFormStatus = "submitted" | "due" | "overdue" | undefined | null;

export interface IFormsCard {
	entity?: GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"];
	caseId: string;
	occurrence: Maybe<OccurrenceEnum>;
	isAddedByEnabledInTable: Maybe<boolean>;
	addedBy: Maybe<Pick<User, "id" | "email" | "firstName" | "lastName" | "isDeleted">>;
	patientAlias?: Maybe<string>;
	formId?: Maybe<string>;
	isEditingFeature: boolean;
	typeId: string;
	title?: Maybe<string>;
	timeLimit?: Maybe<Date>;
	isTimeLimitPassed?: Maybe<boolean>;
	submissionDate?: Maybe<Date>;
	lastUpdatedOn: Maybe<Date>;
	status?: Maybe<FlexFormStatus>;
	timeframeRelation?: TimeframeRangeType;
	index?: number;
	nextForms?: Array<Array<IFormsCard>>;
	isFormOwner?: boolean;
	isCreatable?: boolean;
	caseFormStatus?: CaseFormStatus;
	isFormCreatable?: boolean | null;
	casesCount?: number;
	isStartingPoint?: boolean;
	isDeletionEnabled?: boolean;
}

const FormCardContainer: React.FC = ({ children }) => (
	<Box p={1} width="30%" display="flex" justifyContent="flex-end" alignItems="center">
		{children}
	</Box>
);

const FormCard = (props: IFormsCard) => {
	const [hover, setHover] = useState(false);
	const { formCard, fw500, fw400 } = useStyles();
	const { squareButton } = useMapThemeClasses();
	const { t } = useTranslation();
	const { push } = useHistory();

	function handleViewOrCreate(status?: Maybe<FlexFormStatus>, id?: Maybe<string>, type?: Maybe<string>) {
		if (type) {
			switch (status) {
				case FlexFormStatus.Submitted:
					return handleViewClick(id as string);
				default:
					return handleEditClick(id as string, type);
			}
		}
	}

	function handleEditSubmitted(id: Maybe<string>) {
		push(`/data/forms/${id}?startEdit`);
	}

	function handleEditClick(id: string, type: string) {
		push(id ? `/data/forms/${id}` : `/data/forms/new/${type}?caseId=${props.caseId}`);
	}

	function handleViewClick(id: string) {
		push(`/data/forms/${id}`);
	}

	function renderCaseFormStatusIcon(status: CaseFormStatus | undefined) {
		switch (status) {
			case "due":
				return <DueIcon />;
			case "overdue":
				return <OverdueIcon />;
			default:
				return "";
		}
	}

	return (
		<Paper onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className={formCard} elevation={hover ? 4 : 1}>
			<Box width="70%" display="flex" alignItems="center">
				<Box p={1} width="35%" pl={13}>
					{props.status === FlexFormStatus.Submitted ? (
						<Typography noWrap className={fw500}>
							{t(props.title as string)?.toLocaleUpperCase()}{" "}
						</Typography>
					) : (
						<Typography color="textSecondary" noWrap className={fw500} style={{ color: tableText }}>
							{t(props.title as string)?.toLocaleUpperCase()}{" "}
						</Typography>
					)}
				</Box>
				<Box p={1} width="25%">
					{props.timeLimit && (
						<>
							<Typography noWrap variant="subtitle2" color="textSecondary" className={fw400}>
								{startUpper(t("due"))}
								{props.timeframeRelation === TimeframeRangeType.StartingFrom && " " + t("after")}
							</Typography>
							<Typography>{localeDate(props.timeLimit)}</Typography>
						</>
					)}
				</Box>
				<Box p={1} width="25%">
					{props.status === FlexFormStatus.Submitted && (
						<>
							<Typography noWrap variant="subtitle2" color="textSecondary" className={fw400}>
								{props.submissionDate && startUpper(t("submitted"))}
							</Typography>
							<Typography>{props?.submissionDate && localeDate(props?.submissionDate)}</Typography>
						</>
					)}
				</Box>
			</Box>
			<FormCardContainer>
				{props.caseFormStatus !== undefined && renderCaseFormStatusIcon(props.caseFormStatus)}
				{props.isFormOwner && (props.formId || props.isCreatable) && props.isFormCreatable ? (
					<Box ml={3} display="flex">
						{props.formId && !!props.submissionDate && props.isEditingFeature && (
							<IconButton
								id={`editForm-${props.typeId}`}
								className={squareButton}
								onClick={() => handleEditSubmitted(props.formId!)}
							>
								<EditIcon />
							</IconButton>
						)}
						<IconButton
							id={`addForm-${props.typeId}`}
							className={squareButton}
							onClick={() => handleViewOrCreate(props.status, props.formId, props.typeId)}
						>
							<AddIcon />
						</IconButton>
					</Box>
				) : undefined}
			</FormCardContainer>
		</Paper>
	);
};

export default FormCard;
